import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
  VStack,
  Badge,
} from '@chakra-ui/react';
import { FiRotateCw } from 'react-icons/fi';
import { ExtensionsAPI, UserAPI, extractApiError } from '../../services/api';
import PageHelp from '../../components/PageHelp';
import { useLanguage } from '../../i18n';
import { getPortalPageCopy } from '../../portalPageCopy';

// Concurrent-call limit is read-only here: OneSuite bills on it and writes it via the
// partner API (max_active_calls -> agent_extensions.seat_count). "—" when unknown.
const formatConcurrentCalls = (ext) => {
  const value = ext?.max_active_calls ?? ext?.seat_count;
  return Number.isInteger(value) && value >= 0 ? String(value) : '—';
};

const ExtensionsPage = () => {
  const toast = useToast();
  const { lang } = useLanguage();
  const copy = getPortalPageCopy(lang, 'extensions');

  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extensionStatuses, setExtensionStatuses] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const lastActionTimeRef = useRef({});
  const [pollingExtensions, setPollingExtensions] = useState(new Set());
  const [errorStates, setErrorStates] = useState({});
  const [statusSummary, setStatusSummary] = useState({ total: 0, running: 0, stopped: 0, transitioning: 0, unknown: 0, error: 0 });

  const load = async () => {
    setLoading(true);
    try {
      if (!localStorage.getItem('tenant_uuid')) {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          try {
            const userInfo = await UserAPI.getCurrentUser();
            if (userInfo.tenant_uuid) {
              localStorage.setItem('tenant_uuid', userInfo.tenant_uuid);
              console.log('[Extensions] Fetched and saved tenant_uuid:', userInfo.tenant_uuid);
            }
          } catch (e) {
            console.warn('[Extensions] Failed to fetch tenant_uuid:', e);
          }
        }
      }
      
      const data = await ExtensionsAPI.list();
      const safeList = Array.isArray(data) ? data : [];
      setExtensions(safeList);
      if (safeList.length > 0) {
        await fetchAllStatuses();
      }
    } catch (e) {
      const errorMsg = extractApiError(e);
      toast({
        title: copy.loadFailed,
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (extensions.length === 0) return;
    
    const refreshInterval = setInterval(async () => {
      try {
        await fetchAllStatuses();
      } catch (error) {
        console.warn('Failed to refresh extension statuses:', error);
      }
    }, 10000);

    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensions.length]);

  useEffect(() => {
    if (pollingExtensions.size === 0) return;

    const pollInterval = setInterval(async () => {
      const extensionsToPoll = Array.from(pollingExtensions);
      await Promise.all(
        extensionsToPoll.map(async (extId) => {
          try {
            const status = await ExtensionsAPI.getStatus(extId);
            setExtensionStatuses((prev) => ({ ...prev, [extId]: status }));
            if (status.state === 'running' || status.state === 'stopped' || status.state === 'parked') {
              setPollingExtensions((prev) => {
                const next = new Set(prev);
                next.delete(extId);
                return next;
              });
            }
          } catch (error) {
            console.warn(`Failed to poll status for ${extId}:`, error);
          }
        })
      );
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [pollingExtensions]);

  useEffect(() => {
    const values = Object.values(extensionStatuses || {});
    const summary = values.reduce(
      (acc, item) => {
        const state = (item?.state || 'unknown').toLowerCase();
        if (state === 'running' || state === 'active') acc.running += 1;
        else if (state === 'stopped' || state === 'inactive' || state === 'parked') acc.stopped += 1;
        else if (state === 'starting' || state === 'stopping' || state === 'restarting') acc.transitioning += 1;
        else if (state === 'error') acc.error += 1;
        else acc.unknown += 1;
        return acc;
      },
      { total: values.length, running: 0, stopped: 0, transitioning: 0, unknown: 0, error: 0 }
    );
    setStatusSummary(summary);
  }, [extensionStatuses]);

  const fetchAllStatuses = async () => {
    try {
      const statuses = await ExtensionsAPI.listStatuses();
      const statusMap = {};
      (statuses || []).forEach((status) => {
        statusMap[status.extension] = status;
      });
      setExtensionStatuses(statusMap);
    } catch (error) {
      const errorMsg = extractApiError(error);
      toast({
        title: copy.statusFailed,
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setExtensionStatuses({});
    }
  };

  const ACTION_COOLDOWN_MS = 2000;

  const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        if (attempt === maxRetries - 1) throw error;
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  const canPerformAction = (extension) => {
    const lastTime = lastActionTimeRef.current[extension];
    if (!lastTime) return true;
    const elapsed = Date.now() - lastTime;
    return elapsed >= ACTION_COOLDOWN_MS;
  };

  const handleStart = async (extension) => {
    if (!canPerformAction(extension)) {
      toast({
        title: copy.pleaseWait,
        description: copy.pleaseWaitDetail,
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    lastActionTimeRef.current[extension] = Date.now();
    setActionLoading((prev) => ({ ...prev, [extension]: 'start' }));
    setErrorStates((prev) => ({ ...prev, [extension]: null }));
    setExtensionStatuses((prev) => ({
      ...prev,
      [extension]: { ...prev[extension], state: 'starting', pid: null, last_seen: null },
    }));
    setPollingExtensions((prev) => {
      const next = new Set(prev);
      next.add(extension);
      return next;
    });

    try {
      const result = await retryWithBackoff(async () => {
        return await ExtensionsAPI.start(extension);
      });
      
      if (result) {
        setExtensionStatuses((prev) => ({
          ...prev,
          [extension]: {
            state: result.state || 'starting',
            pid: result.pid,
            last_seen: result.last_seen,
          },
        }));
      }

      toast({
        title: `${copy.extension} ${extension} ${copy.started}`,
        status: 'success',
        duration: 3000,
      });
      
      setTimeout(() => {
        load();
      }, 1000);
    } catch (error) {
      const errorMsg = extractApiError(error);
      setErrorStates((prev) => ({ ...prev, [extension]: errorMsg }));
      setExtensionStatuses((prev) => ({
        ...prev,
        [extension]: { ...prev[extension], state: 'error', pid: null, last_seen: null },
      }));
      setPollingExtensions((prev) => {
        const next = new Set(prev);
        next.delete(extension);
        return next;
      });
      toast({
        title: copy.startFailed,
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [extension]: null }));
    }
  };

  const handleStop = async (extension) => {
    if (!canPerformAction(extension)) {
      toast({
        title: copy.pleaseWait,
        description: copy.pleaseWaitDetail,
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    lastActionTimeRef.current[extension] = Date.now();
    setActionLoading((prev) => ({ ...prev, [extension]: 'stop' }));
    setErrorStates((prev) => ({ ...prev, [extension]: null }));
    setExtensionStatuses((prev) => ({
      ...prev,
      [extension]: { ...prev[extension], state: 'stopping', pid: null, last_seen: null },
    }));
    setPollingExtensions((prev) => {
      const next = new Set(prev);
      next.add(extension);
      return next;
    });

    try {
      const result = await retryWithBackoff(async () => {
        return await ExtensionsAPI.stop(extension);
      });
      
      if (result) {
        setExtensionStatuses((prev) => ({
          ...prev,
          [extension]: {
            state: result.state || 'stopped',
            pid: result.pid,
            last_seen: result.last_seen,
          },
        }));
      }

      toast({
        title: `${copy.extension} ${extension} ${copy.stoppedAction}`,
        status: 'success',
        duration: 3000,
      });
      
      setTimeout(() => {
        load();
      }, 1000);
    } catch (error) {
      const errorMsg = extractApiError(error);
      setErrorStates((prev) => ({ ...prev, [extension]: errorMsg }));
      setExtensionStatuses((prev) => ({
        ...prev,
        [extension]: { ...prev[extension], state: 'error', pid: null, last_seen: null },
      }));
      setPollingExtensions((prev) => {
        const next = new Set(prev);
        next.delete(extension);
        return next;
      });
      toast({
        title: copy.stopFailed,
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [extension]: null }));
    }
  };

  const handleRestart = async (extension) => {
    if (!canPerformAction(extension)) {
      toast({
        title: copy.pleaseWait,
        description: copy.pleaseWaitDetail,
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    lastActionTimeRef.current[extension] = Date.now();
    setActionLoading((prev) => ({ ...prev, [extension]: 'restart' }));
    setErrorStates((prev) => ({ ...prev, [extension]: null }));
    setExtensionStatuses((prev) => ({
      ...prev,
      [extension]: { ...prev[extension], state: 'restarting', pid: null, last_seen: null },
    }));
    setPollingExtensions((prev) => {
      const next = new Set(prev);
      next.add(extension);
      return next;
    });

    try {
      const result = await retryWithBackoff(async () => {
        return await ExtensionsAPI.restart(extension);
      });
      
      if (result) {
        setExtensionStatuses((prev) => ({
          ...prev,
          [extension]: {
            state: result.state || 'running',
            pid: result.pid,
            last_seen: result.last_seen,
          },
        }));
      }

      toast({
        title: `${copy.extension} ${extension} ${copy.restarted}`,
        status: 'success',
        duration: 3000,
      });
      
      setTimeout(() => {
        load();
      }, 1000);
    } catch (error) {
      const errorMsg = extractApiError(error);
      setErrorStates((prev) => ({ ...prev, [extension]: errorMsg }));
      setExtensionStatuses((prev) => ({
        ...prev,
        [extension]: { ...prev[extension], state: 'error', pid: null, last_seen: null },
      }));
      setPollingExtensions((prev) => {
        const next = new Set(prev);
        next.delete(extension);
        return next;
      });
      toast({
        title: copy.restartFailed,
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [extension]: null }));
    }
  };

  const renderStatusBadge = (state) => {
    const stateStr = (state || 'unknown').toLowerCase();
    if (stateStr === 'running' || stateStr === 'active') {
      return <Badge colorScheme="green">{copy.running}</Badge>;
    }
    if (stateStr === 'stopped' || stateStr === 'inactive') {
      return <Badge colorScheme="gray">{copy.stopped}</Badge>;
    }
    if (stateStr === 'starting' || stateStr === 'stopping' || stateStr === 'restarting') {
      return <Badge colorScheme="yellow">{copy.transitioning}</Badge>;
    }
    if (stateStr === 'parked') {
      return <Badge colorScheme="gray">{copy.parked}</Badge>;
    }
    if (stateStr === 'error') {
      return <Badge colorScheme="red">{copy.error}</Badge>;
    }
    return <Badge colorScheme="gray">{copy.unknown}</Badge>;
  };

  const renderRow = (ext) => {
    const status = extensionStatuses[ext.extension] || {};
    const state = status.state || 'unknown';
    const isLoading = actionLoading[ext.extension];
    const error = errorStates[ext.extension];

    return (
      <Box
        key={ext.extension}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        bg="white"
        p={3}
      >
        <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={3} minW={0}>
        <Box minW={0}>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">{copy.extensionDomain}</Text>
          <VStack align="start" spacing={0}>
            <Text fontWeight="semibold">{ext.extension}</Text>
            <Text fontSize="xs" color="gray.500" wordBreak="break-all">{ext.sip_domain || copy.notAvailable}</Text>
          </VStack>
        </Box>
        <Box minW={0} title={copy.concurrentCallsHint}>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">{copy.concurrentCalls}</Text>
          <Text fontWeight="semibold" data-testid={`concurrent-calls-${ext.extension}`}>
            {formatConcurrentCalls(ext)}
          </Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">{copy.status}</Text>
          <VStack align="start" spacing={1}>
            {renderStatusBadge(state)}
            {error && (
              <Text fontSize="xs" color="red.500" wordBreak="break-word">
                {error}
              </Text>
            )}
          </VStack>
        </Box>
        <Box minW={0}>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">{copy.runtime}</Text>
          <Badge colorScheme="green">{copy.alwaysOn}</Badge>
        </Box>
        <Box minW={0}>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">{copy.actions}</Text>
          <HStack spacing={1} flexWrap="wrap">
            <Button
              size="xs"
              colorScheme="green"
              onClick={() => handleStart(ext.extension)}
              isLoading={isLoading === 'start'}
              isDisabled={!!isLoading || state === 'running'}
              minW="55px"
              title={copy.startTitle}
            >
              {copy.start}
            </Button>
            <Button
              size="xs"
              colorScheme="red"
              onClick={() => handleStop(ext.extension)}
              isLoading={isLoading === 'stop'}
              isDisabled={!!isLoading || state === 'stopped'}
              minW="50px"
              title={copy.stopTitle}
            >
              {copy.stop}
            </Button>
            <Button
              size="xs"
              colorScheme="blue"
              onClick={() => handleRestart(ext.extension)}
              isLoading={isLoading === 'restart'}
              isDisabled={!!isLoading}
              minW="60px"
              title={copy.restartTitle}
            >
              {copy.restart}
            </Button>
          </HStack>
        </Box>
        </SimpleGrid>
      </Box>
    );
  };

  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column" overflow="hidden">
      <Box flexShrink={0}>
        <PageHelp
          title={copy.helpTitle}
          description={copy.helpDescription}
          steps={[
            { title: copy.start, detail: copy.startHelp },
            { title: copy.stop, detail: copy.stopHelp },
            { title: copy.restart, detail: copy.restartHelp },
            { title: copy.refreshStatus, detail: copy.refreshHelp },
          ]}
        />
      </Box>

      <Box flex="1" minH={0} overflowY="auto">
        <Card mb={6}>
          <CardBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              {copy.runtimeWarning}
            </Text>
            <Flex justify="space-between" align="center" mb={4}>
              <HStack spacing={4}>
                <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                  {copy.total}: {statusSummary.total}
                </Badge>
                {statusSummary.running > 0 && (
                  <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                    {copy.running}: {statusSummary.running}
                  </Badge>
                )}
                {statusSummary.stopped > 0 && (
                  <Badge colorScheme="gray" fontSize="md" px={3} py={1}>
                    {copy.stopped}: {statusSummary.stopped}
                  </Badge>
                )}
                {statusSummary.transitioning > 0 && (
                  <Badge colorScheme="yellow" fontSize="md" px={3} py={1}>
                    {copy.transitioning}: {statusSummary.transitioning}
                  </Badge>
                )}
                {statusSummary.error > 0 && (
                  <Badge colorScheme="red" fontSize="md" px={3} py={1}>
                    {copy.error}: {statusSummary.error}
                  </Badge>
                )}
                {statusSummary.unknown > 0 && (
                  <Badge colorScheme="gray" fontSize="md" px={3} py={1}>
                    {copy.unknown}: {statusSummary.unknown}
                  </Badge>
                )}
              </HStack>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  leftIcon={<FiRotateCw />}
                  onClick={load}
                  isLoading={loading}
                >
                  {copy.refreshStatus}
                </Button>
              </HStack>
            </Flex>
          </CardBody>
        </Card>

        <Card mb={6}>
          <CardHeader>
            <Heading size="md">{copy.extensions}</Heading>
            <Text mt={2} fontSize="sm" color="text.secondary">{copy.concurrentCallsHint}</Text>
          </CardHeader>
          <CardBody>
            {extensions.length === 0 ? (
              <Box textAlign="center" py={8}>
                {loading ? <Spinner /> : copy.noExtensions}
              </Box>
            ) : (
              <VStack spacing={3} align="stretch">
                {extensions.map(renderRow)}
              </VStack>
            )}
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};

export default ExtensionsPage;
